import { compareAsc, format, isValid, parseISO, startOfToday } from 'date-fns';
import isEqual from 'lodash.isequal';
import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import cloneDeep from 'lodash.clonedeep';

import { IHistoricalValue } from 'common/src/types/interfaces';

import { dateOnlyFormat } from 'portal/src/validators/dateValidator';

export interface UseHistoricalValuesReturn<T> {
  data: IHistoricalValue<T>[] | null;
  isDirty: boolean;
  errorStr: string | null;
  open: boolean;
  formatPayload: () => IHistoricalValue<T>[] | null;
  handleDataChange: (data: IHistoricalValue<T>[]) => void;
  handleOpen: () => void;
  handleClose: () => void;
  handleSave: (data: IHistoricalValue<T>[]) => void;
  handleValueChange: (event: ChangeEvent<HTMLInputElement> | T) => void;
}

function parseDateValue(value?: Date | string | null): Date | null {
  if (!value) {
    return null;
  }

  const parsed = typeof value === 'string' ? parseISO(value) : value;

  return isValid(parsed) ? parsed : null;
}

function hasValidValue<T>(item: IHistoricalValue<T>) {
  return item.value != null && item.value !== '';
}

function buildDataWithTargetValue<T>(
  targetFieldValue: T,
  sourceData: IHistoricalValue<T>[],
  historyStartDate: string | undefined,
): IHistoricalValue<T>[] {
  // If there are no historical values, add a placeholder
  if (sourceData.length === 0) {
    if (targetFieldValue === null || targetFieldValue === undefined) {
      return [];
    }

    return [
      {
        value: targetFieldValue,
        startDate: format(
          parseDateValue(historyStartDate) ?? new Date(),
          dateOnlyFormat,
        ),
        endDate: null,
      },
    ];
  }

  const lastEntry = sourceData.at(-1);

  // If there is only one entry and the value is not valid, start the history from today
  if (sourceData.length === 1 && !hasValidValue(lastEntry)) {
    return [
      {
        value: targetFieldValue,
        startDate: format(new Date(), dateOnlyFormat),
        endDate: null,
      },
    ];
  }

  // Use loose equality to allow type coercion for scenarios like:
  // target value: 100, source value: '100.00'
  const isLooselyEqual = (sourceValue: T, targetValue: T) =>
    sourceValue == targetValue;

  if (lastEntry && isLooselyEqual(lastEntry.value, targetFieldValue)) {
    // PATCH: dedupe identical entries which occurred because of previous bugs
    let patchedData = sourceData;

    for (let i = patchedData.length - 2; i >= 0; i--) {
      if (isLooselyEqual(patchedData[i].value, targetFieldValue)) {
        patchedData = [...patchedData.slice(0, i), lastEntry];
      } else {
        break;
      }
    }

    return patchedData;
  }

  const today = startOfToday();
  const lastEntryStartDate = parseDateValue(lastEntry?.startDate);

  // If there is another change on the same day as the last entry, replace the previous entry value with the new value
  if (
    lastEntry &&
    lastEntryStartDate &&
    format(lastEntryStartDate, dateOnlyFormat) === format(today, dateOnlyFormat)
  ) {
    return [
      ...sourceData.slice(0, -1),
      {
        ...lastEntry,
        value: targetFieldValue,
      },
    ];
  }

  // If this is a new value on a new day, set the last entry's end date and add the value to the history
  const updatedLastEntry = {
    ...lastEntry,
    endDate: format(today, dateOnlyFormat),
  };

  return [
    ...sourceData.slice(0, -1),
    updatedLastEntry,
    {
      value: targetFieldValue,
      startDate: format(new Date(), dateOnlyFormat),
      endDate: null,
    },
  ];
}

function sortAndFormatHistoricalValues<T>(
  newData: IHistoricalValue<T>[],
): IHistoricalValue<T>[] {
  const sortedData = [...newData].sort((a, b) => {
    const aDate = parseDateValue(a.startDate);
    const bDate = parseDateValue(b.startDate);

    if (!aDate && !bDate) return 0;
    if (!aDate) return 1;
    if (!bDate) return -1;

    return compareAsc(aDate, bDate);
  });

  return sortedData.map((item) => {
    const startDate = parseDateValue(item.startDate);
    const endDate = parseDateValue(item.endDate);

    return {
      ...item,
      startDate: startDate ? format(startDate, dateOnlyFormat) : null,
      endDate: endDate ? format(endDate, dateOnlyFormat) : null,
    };
  });
}

function normalizeHistoricalValues<T>(
  newData: IHistoricalValue<T>[],
): IHistoricalValue<T>[] {
  let data = sortAndFormatHistoricalValues(newData);

  // Align end dates with start date of next record
  data = data.map((item, index) => ({
    ...item,
    endDate: data[index + 1]?.startDate ?? null,
  }));

  return data;
}

/**
 * Custom hook to manage a targeted field's historical values, including dialog
 * state and change tracking.
 */
export function useHistoricalValues<T>(
  initialValue: T,
  initialData: IHistoricalValue<T>[] | null,
  historyStartDate: string | undefined,
): UseHistoricalValuesReturn<T> {
  const onFirstRenderData = useMemo(
    () =>
      buildDataWithTargetValue(
        initialValue,
        initialData ?? [],
        historyStartDate,
      ),
    [initialData, initialValue, historyStartDate],
  );

  const [open, setOpen] = useState(false);
  const [data, setData] = useState<IHistoricalValue<T>[]>(onFirstRenderData);
  const snapshotBeforeOpenRef = useRef<IHistoricalValue<T>[] | null>(null);

  const isDirty = useMemo(
    () => !isEqual(data, onFirstRenderData),
    [data, onFirstRenderData],
  );

  const errorStr = useMemo(() => {
    const hasDateGaps = data.slice(0, -1).some((item, index) => {
      const nextItem = data[index + 1];
      return item.endDate !== nextItem.startDate;
    });

    if (hasDateGaps) {
      return 'The "To Date" of each row must equal the "From Date" of the following row.';
    }

    return null;
  }, [data]);

  const formatPayload = useCallback(() => {
    const payload = cloneDeep(data);

    // If the last items's value is null or an empty string, remove it from the pay. This allows the
    // value to be empty but not cause history validation errors in the API
    const lastItem = payload.at(-1);
    if (lastItem && !hasValidValue(lastItem)) {
      payload.pop();
    }

    if (!payload.length) {
      return null;
    }

    return payload;
  }, [data]);

  const handleOpen = useCallback(() => {
    snapshotBeforeOpenRef.current = data;
    setOpen(true);
  }, [data]);

  const handleClose = useCallback(() => {
    if (snapshotBeforeOpenRef.current !== null) {
      setData(snapshotBeforeOpenRef.current);
    }

    snapshotBeforeOpenRef.current = null;
    setOpen(false);
  }, []);

  const handleDataChange = useCallback((updatedData: IHistoricalValue<T>[]) => {
    setData(sortAndFormatHistoricalValues(updatedData));
  }, []);

  const handleSave = useCallback((updatedData: IHistoricalValue<T>[]) => {
    const normalizedData = normalizeHistoricalValues(updatedData);
    setData(normalizedData);
    snapshotBeforeOpenRef.current = null;
    setOpen(false);
  }, []);

  const handleValueChange = useCallback(
    (event: ChangeEvent<HTMLInputElement> | T) => {
      let newValue: T;
      if (event == null) {
        newValue = null;
      } else if ((event as ChangeEvent<HTMLInputElement>).target) {
        newValue = (event as ChangeEvent<HTMLInputElement>).target.value as T;
      } else {
        newValue = event as T;
      }

      setData((currentData) => {
        const lastEntry = currentData.at(-1);

        if (lastEntry && lastEntry.value === newValue) {
          return currentData;
        }
        return buildDataWithTargetValue(
          newValue,
          currentData,
          historyStartDate,
        );
      });
    },
    [historyStartDate],
  );

  return {
    data,
    isDirty,
    errorStr,
    open,
    formatPayload,
    handleDataChange,
    handleOpen,
    handleClose,
    handleSave,
    handleValueChange,
  };
}
